import moment from "moment";
import { parseBooleanVal } from "Utils/utils";
import { fetchSilHomeOptionById } from "../hooks/useAdvanceSilHomesSearch";
import {
  applyParticipantTravelToSplits,
  buildAdvanceShiftCreatePayload,
  claimTypeFromIsTravel,
  createEmptyClientSplit,
  createOpenSlotRow,
  createWorkerCarerRow,
  extractCommonResourceWorkerLists,
  mapCommonWorkerToOption,
  mapSilHomeParticipantToOption,
  normalizeAdvanceShiftTaskForForm,
  resolveKmForInvoicingValue,
  resolveStaValue,
} from "./advanceModeSplitDefaults";

const extractSilHomeParticipantsList = (response) => {
  const root = response?.data ?? response;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (root && typeof root === "object") {
    const list = Object.values(root).find((v) => Array.isArray(v));
    if (list) return list;
  }
  return [];
};

const DATETIME_FORMATS = ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601];

const parseDateTime = (value) => {
  if (!value) return null;
  const strict = moment(value, DATETIME_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose = moment(value);
  return loose.isValid() ? loose : null;
};

const toClockTime = (value) => {
  const parsed = parseDateTime(value);
  return parsed ? parsed.format("HH:mm") : "";
};

const toWallClockTime = (value) => {
  if (!value) return "";
  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const toShiftDate = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true
  );
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  const loose = moment(value);
  return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
};

const parseShiftLocation = (raw) => {
  if (!raw) return null;
  let location = raw;
  if (typeof location === "string") {
    try {
      location = JSON.parse(location);
    } catch (e) {
      return null;
    }
  }
  if (!location || typeof location !== "object") return null;
  const street = location.street || location.street_address || "";
  const suburb = location.suburb || "";
  if (!street && !suburb) return null;
  return {
    street,
    suburb,
    state: location.state ?? null,
    state_name: location.state_name || "",
    postcode: location.postcode || "",
    lat: location.lat ?? null,
    lng: location.lng ?? null,
  };
};

const stubParticipantOption = (id) => ({
  id: Number(id),
  label: `Participant #${id}`,
  value: String(id),
  first_name: "Participant",
  last_name: `#${id}`,
});

const stubWorkerOption = (id) => ({
  id: Number(id),
  label: `Worker #${id}`,
  value: String(id),
  first_name: "Worker",
  last_name: `#${id}`,
});

const resolveParticipantOption = (id, lookup = []) => {
  if (id == null) return null;
  const match = (lookup || []).find(
    (p) =>
      Number(p?.id) === Number(id) ||
      String(p?.value) === String(id) ||
      String(p?.uuid) === String(id)
  );
  if (match) {
    const mapped = mapSilHomeParticipantToOption(match) || match;
    // Prefer real options over stub-shaped rows that may also be in the lookup
    const label = String(mapped?.label || "").trim();
    if (!/^Participant #\d+$/i.test(label)) {
      return mapped;
    }
    const better = (lookup || []).find(
      (p) =>
        Number(p?.id) === Number(id) &&
        p !== match &&
        !/^Participant #\d+$/i.test(String(p?.label || "").trim())
    );
    if (better) return mapSilHomeParticipantToOption(better) || better;
    return mapped;
  }
  return stubParticipantOption(id);
};

const resolveWorkerOption = (id, lookup = []) => {
  if (id == null) return null;
  const matches = (lookup || []).filter(
    (w) =>
      Number(w?.id) === Number(id) ||
      String(w?.value) === String(id) ||
      String(w?.uuid) === String(id)
  );
  const match =
    matches.find((w) => {
      const label = String(w?.label || "").trim();
      const first = String(w?.first_name || "").trim();
      const last = String(w?.last_name || "").trim();
      if (/^Worker #\d+$/i.test(label)) return false;
      if (first === "Worker" && /^#\d+$/.test(last)) return false;
      return Boolean(
        w?.uuid || w?.first_name || w?.last_name || (label && !/^Worker #\d+$/i.test(label))
      );
    }) || matches[0];
  if (match) {
    return mapCommonWorkerToOption(match) || match;
  }
  return stubWorkerOption(id);
};

const isParticipantSplitsShape = (splits) =>
  Array.isArray(splits) &&
  splits.length > 0 &&
  splits.some(
    (row) =>
      row &&
      (row.participant_id != null || Array.isArray(row.splits))
  );

const isFlatFormSplitsShape = (splits) =>
  Array.isArray(splits) &&
  splits.length > 0 &&
  splits.some(
    (row) =>
      row &&
      (Array.isArray(row.participant_ids) ||
        row.service_type_id != null ||
        row.split_shift_id != null)
  );

/**
 * Expand draft `form.splits` (participant_splits | flat form splits | UI rows)
 * into advance_splits field-array rows.
 */
const expandDraftSplits = (splits, participantLookup, sta) => {
  if (!Array.isArray(splits) || splits.length === 0) return [];

  if (isParticipantSplitsShape(splits)) {
    return splits.flatMap((group) => {
      const participant = resolveParticipantOption(
        group.participant_id,
        participantLookup
      );
      const segments = Array.isArray(group.splits) ? group.splits : [];
      if (!segments.length) {
        const base = createEmptyClientSplit(participant, {});
        return [{ ...base, participant, participants: participant ? [participant] : [], sta }];
      }
      return segments.map((segment) => {
        const base = createEmptyClientSplit(participant, {});
        return {
          ...base,
          participant,
          participants: participant ? [participant] : [],
          participant_start_time: toClockTime(segment.start_time),
          participant_end_time: toClockTime(segment.end_time),
          invoice_service_type_id: segment.service_type_id ?? null,
          invoice_item_id: segment.invoice_item_id ?? null,
          custom_line_item_uuid:
            segment.custom_line_item_uuid ??
            segment.custom_line_item?.uuid ??
            null,
          support_item_number:
            segment.ndis_support_item_number ||
            segment.support_item_number ||
            "",
          support_item_name:
            segment.ndis_support_item_name ||
            segment.support_item_name ||
            segment.custom_line_item?.description ||
            "",
          ratio: segment.ratio || "1:1",
          // Older drafts have no flag — leave it unset so it is derived from the item name.
          ...(segment.is_sleepover !== undefined && segment.is_sleepover !== null
            ? { is_sleepover: parseBooleanVal(segment.is_sleepover) }
            : {}),
          km_for_invoicing: resolveKmForInvoicingValue(
            segment.km_for_invoicing ?? segment.apply_km
          ),
          default_km_code:
            segment.default_km_code !== undefined
              ? segment.default_km_code
              : null,
          claim_type:
            segment.claim_type ??
            (segment.is_travel != null
              ? claimTypeFromIsTravel(segment.is_travel)
              : null),
          sta,
        };
      });
    });
  }

  if (isFlatFormSplitsShape(splits)) {
    return splits.flatMap((split) => {
      const ids =
        Array.isArray(split.participant_ids) && split.participant_ids.length
          ? split.participant_ids
          : [null];
      return ids.map((pid) => {
        const participant = pid != null
          ? resolveParticipantOption(pid, participantLookup)
          : null;
        const base = createEmptyClientSplit(participant, {});
        return {
          ...base,
          participant,
          participants: participant ? [participant] : [],
          participant_start_time: toClockTime(split.start_time),
          participant_end_time: toClockTime(split.end_time),
          invoice_service_type_id: split.service_type_id ?? null,
          invoice_item_id: split.invoice_item_id ?? null,
          custom_line_item_uuid:
            split.custom_line_item_uuid ??
            split.custom_line_item?.uuid ??
            null,
          support_item_number:
            split.ndis_support_item_number || split.support_item_number || "",
          support_item_name:
            split.ndis_support_item_name ||
            split.support_item_name ||
            split.custom_line_item?.description ||
            "",
          ratio: split.ratio || "1:1",
          // Older drafts have no flag — leave it unset so it is derived from the item name.
          ...(split.is_sleepover !== undefined && split.is_sleepover !== null
            ? { is_sleepover: parseBooleanVal(split.is_sleepover) }
            : {}),
          km_for_invoicing: resolveKmForInvoicingValue(
            split.km_for_invoicing ?? split.apply_km
          ),
          default_km_code:
            split.default_km_code !== undefined ? split.default_km_code : null,
          claim_type:
            split.claim_type ??
            (split.is_travel != null
              ? claimTypeFromIsTravel(split.is_travel)
              : null),
          sta,
        };
      });
    });
  }

  // Already UI-shaped advance_splits
  return splits;
};

/**
 * Prefill advance builder from GET /shift-drafts/{uuid} `form` (+ optional payload).
 */
export const parseAdvanceDraftForm = (form, options = {}) => {
  if (!form || typeof form !== "object") return null;

  const participantLookup = [
    ...(options.participantLookup || []),
    ...(Array.isArray(form.participants) ? form.participants : []),
  ];
  const workerLookup = options.workerLookup || [];
  const sta = resolveStaValue(form.is_sta ?? form.sta);
  const isOpenShift = parseBooleanVal(
    form.is_open_shift ?? form.open_shift ?? form.advance_open_shift
  );

  const participantIds = Array.isArray(form.participant_ids)
    ? form.participant_ids
    : [];

  // Prefer hydrated form.participants snapshots (uuid + names) when present
  const embeddedById = new Map(
    (Array.isArray(form.participants) ? form.participants : [])
      .filter((p) => p?.id != null)
      .map((p) => [String(p.id), p])
  );

  const advance_selected_participants = participantIds
    .map((id) => {
      const embedded = embeddedById.get(String(id));
      if (embedded?.uuid && (embedded.first_name || embedded.last_name || embedded.label)) {
        return mapSilHomeParticipantToOption(embedded) || embedded;
      }
      return resolveParticipantOption(id, participantLookup);
    })
    .filter(Boolean);

  let advance_splits = applyParticipantTravelToSplits(
    expandDraftSplits(form.splits, participantLookup, sta),
    form.participant_travel
  );

  // Ensure every selected participant has at least one split row when none stored
  if (
    advance_splits.length === 0 &&
    advance_selected_participants.length > 0
  ) {
    advance_selected_participants.forEach((participant) => {
      const base = createEmptyClientSplit(participant, {
        start_time: toClockTime(form.start_time),
        end_time: toClockTime(form.end_time),
      });
      advance_splits.push({
        ...base,
        participant,
        participants: [participant],
        sta,
      });
    });
    advance_splits = applyParticipantTravelToSplits(
      advance_splits,
      form.participant_travel
    );
  }

  const workers = Array.isArray(form.workers) ? form.workers : [];
  const advance_carers = workers.map((assignment) => {
    const isOpen =
      parseBooleanVal(assignment.is_open_slot ?? assignment.is_open) ||
      assignment.worker_id == null;
    if (isOpen) {
      const row = createOpenSlotRow({
        start_time: toWallClockTime(assignment.start_time ?? form.start_time),
        end_time: toWallClockTime(assignment.end_time ?? form.end_time),
      });
      return {
        ...row,
        sleepover: parseBooleanVal(
          assignment.sleepover ?? assignment.is_sleepover ?? false
        ),
        active_hours: assignment.active_hours ?? assignment.activeHours ?? "",
      };
    }
    const worker =
      assignment.worker
        ? mapCommonWorkerToOption({
            ...assignment.worker,
            profile_picture:
              assignment.worker.profile_picture ??
              assignment.worker.photo ??
              null,
          }) || resolveWorkerOption(assignment.worker_id, workerLookup)
        : resolveWorkerOption(assignment.worker_id, workerLookup);
    return {
      ...createWorkerCarerRow(worker, {
        start_time: toWallClockTime(assignment.start_time ?? form.start_time),
        end_time: toWallClockTime(assignment.end_time ?? form.end_time),
      }),
      sleepover: parseBooleanVal(
        assignment.sleepover ?? assignment.is_sleepover ?? false
      ),
      active_hours: assignment.active_hours ?? assignment.activeHours ?? "",
      assignment_id: assignment.assignment_id ?? assignment.id ?? null,
      worker_id: assignment.worker_id ?? worker?.id ?? null,
      status: assignment.status ?? null,
      progress: assignment.progress ?? null,
      shift_accept: assignment.shift_accept ?? assignment.shiftAccept ?? null,
    };
  });

  const advance_selected_workers = advance_carers
    .map((row) => row.worker)
    .filter(Boolean);

  const tasks = (Array.isArray(form.shift_tasks) ? form.shift_tasks : []).map(
    (task) => normalizeAdvanceShiftTaskForForm(task)
  );
  const notes = form.note ? [{ notes: form.note }] : [];

  const silHomeId = form.sil_home_id ?? null;
  const silHomeUuid =
    options.silHomeUuid ||
    form.sil_home_uuid ||
    form.sil_home?.uuid ||
    null;
  const silHomeLabel =
    options.silHomeLabel ||
    form.sil_home_label ||
    form.sil_home?.label ||
    null;

  return {
    shift_date: toShiftDate(form.shift_date),
    start_time: toClockTime(form.start_time),
    end_time: toClockTime(form.end_time),
    location_tracking: parseBooleanVal(form.location_tracking),
    is_signature: parseBooleanVal(form.is_signature),
    is_radius: parseBooleanVal(form.is_radius),
    sleepover: parseBooleanVal(form.sleepover ?? form.is_sleepover ?? false),
    sta,
    shift_location: parseShiftLocation(form.shift_location),
    advance_open_shift: isOpenShift,
    advance_selected_participants,
    advance_selected_workers,
    advance_splits,
    advance_carers,
    advance_sil_home_enabled: silHomeId != null,
    advance_sil_home_id: silHomeId,
    advance_sil_home_uuid: silHomeUuid,
    advance_sil_home_label: silHomeLabel,
    tasks,
    notes,
    shiftNotes: [],
    shift_instructions: Array.isArray(form.shift_instructions)
      ? form.shift_instructions
      : [],
  };
};

/** Unwrap GET/POST/PUT draft API envelopes. */
export const unwrapAdvanceDraftResponse = (response) => {
  if (!response || typeof response !== "object") return null;
  return (
    response.advance_shift_draft ||
    response.data?.advance_shift_draft ||
    response.data ||
    response
  );
};

const isNumericStubId = (value) =>
  value != null && /^\d+$/.test(String(value).trim());

const assignmentNeedsWorkerLookup = (assignment) => {
  if (!assignment || typeof assignment !== "object") return false;
  const isOpen =
    parseBooleanVal(assignment.is_open_slot ?? assignment.is_open) ||
    assignment.worker_id == null;
  if (isOpen) return false;
  const worker = assignment.worker;
  if (!worker) return true;
  const name =
    `${worker.first_name || ""} ${worker.last_name || ""}`.trim() ||
    worker.label ||
    "";
  if (!name || /^Worker #\d+$/i.test(String(name))) return true;
  if (!worker.uuid) return true;
  return false;
};

const participantNeedsLookup = (participant) => {
  if (!participant) return true;
  const name =
    `${participant.first_name || ""} ${participant.last_name || ""}`.trim() ||
    participant.label ||
    "";
  if (!name || /^Participant #\d+$/i.test(String(name))) return true;
  if (!participant.uuid) return true;
  return false;
};

/**
 * Before mounting the draft form, resolve worker/participant lookups so chips
 * and cards never flash `Worker #<id>` / `Participant #<id>`.
 * Uses embedded form snapshots first, then SIL-home participants (Home
 * Management drafts), then common-resources for workers.
 */
export const resolveDraftLookupsBeforeParse = async ({
  form,
  rosteringApi,
  participantLookup = [],
  workerLookup = [],
} = {}) => {
  const embeddedParticipants = Array.isArray(form?.participants)
    ? form.participants
    : [];
  const embeddedWorkers = (Array.isArray(form?.workers) ? form.workers : [])
    .map((assignment) => assignment?.worker)
    .filter(Boolean);

  let mergedParticipantLookup = [
    ...embeddedParticipants,
    ...(participantLookup || []),
  ];
  let mergedWorkerLookup = [...embeddedWorkers, ...(workerLookup || [])];
  let silHomeUuid = form?.sil_home_uuid || form?.sil_home?.uuid || null;
  let silHomeLabel = form?.sil_home_label || form?.sil_home?.label || null;
  let homeParticipantOptions = [];

  const ptIds = Array.isArray(form?.participant_ids) ? form.participant_ids : [];
  const participantsNeedNames =
    ptIds.some((id) => {
      const embedded = embeddedParticipants.find(
        (p) => Number(p?.id) === Number(id)
      );
      if (embedded && !participantNeedsLookup(embedded)) return false;
      const fromLookup = mergedParticipantLookup.find(
        (p) => Number(p?.id) === Number(id)
      );
      return participantNeedsLookup(fromLookup);
    }) ||
    (Array.isArray(form?.participants) &&
      form.participants.some(participantNeedsLookup));

  const workersNeedNames = (Array.isArray(form?.workers) ? form.workers : []).some(
    assignmentNeedsWorkerLookup
  );

  const silHomeId = form?.sil_home_id ?? form?.sil_home?.id ?? null;
  const needsSilHomeParticipants =
    silHomeId != null &&
    (participantsNeedNames || workersNeedNames || !silHomeUuid);

  // Home Management drafts often only store numeric participant_ids. Prefetch
  // the home roster BEFORE parse so selects mount with real names/uuids.
  if (
    needsSilHomeParticipants &&
    typeof rosteringApi?.getSilHomeParticipants === "function"
  ) {
    try {
      // Only scan sil-homes when uuid is missing — label is optional for
      // participant name resolution and can hydrate later in HomeSection.
      if (!silHomeUuid) {
        const homeOption = await fetchSilHomeOptionById(silHomeId);
        if (homeOption) {
          silHomeUuid = homeOption.uuid || silHomeUuid;
          silHomeLabel = homeOption.label || silHomeLabel;
        }
      }

      if (silHomeUuid) {
        const res = await rosteringApi.getSilHomeParticipants(silHomeUuid);
        const raw = extractSilHomeParticipantsList(res);
        homeParticipantOptions = raw
          .map(mapSilHomeParticipantToOption)
          .filter(Boolean);
        if (homeParticipantOptions.length) {
          mergedParticipantLookup = [
            ...homeParticipantOptions,
            ...mergedParticipantLookup,
          ];
        }
      }
    } catch {
      // Keep embedded / existing lookups; UI may still upgrade after mount
    }
  }

  const collectParticipantUuids = () => {
    const fromLookup = (mergedParticipantLookup || [])
      .map((p) => p?.uuid || p?.value)
      .filter((uuid) => uuid && !isNumericStubId(uuid));
    const fromIds = ptIds
      .map((id) => {
        const match = mergedParticipantLookup.find(
          (p) =>
            Number(p?.id) === Number(id) ||
            String(p?.value) === String(id) ||
            String(p?.uuid) === String(id)
        );
        const uuid = match?.uuid || match?.value;
        if (!uuid || isNumericStubId(uuid)) return null;
        return uuid;
      })
      .filter(Boolean);
    return [...new Set([...fromLookup, ...fromIds])];
  };

  const uniquePtUuids = collectParticipantUuids();

  // When participants have real UUIDs, common-resources fills missing workers.
  if (
    workersNeedNames &&
    uniquePtUuids.length &&
    typeof rosteringApi?.postCommonResources === "function"
  ) {
    try {
      const res = await rosteringApi.postCommonResources({
        type: "participant",
        uuids: uniquePtUuids,
      });
      const { commonWorkers, allParticipantWorkers } =
        extractCommonResourceWorkerLists(res);
      mergedWorkerLookup = [
        ...(commonWorkers || []),
        ...(allParticipantWorkers || []),
        ...mergedWorkerLookup,
      ];
    } catch {
      // Keep embedded / existing lookups
    }
  }

  return {
    participantLookup: mergedParticipantLookup,
    workerLookup: mergedWorkerLookup,
    silHomeUuid,
    silHomeLabel,
    homeParticipantOptions,
  };
};

const omitEmptyDateTime = (value) => {
  if (value == null || value === "") return undefined;
  return value;
};

/**
 * Same shape as advance create, but drops empty datetimes so draft validation
 * (nullable dates) accepts incomplete builders.
 */
export const buildAdvanceShiftDraftPayload = (args) => {
  const payload = buildAdvanceShiftCreatePayload(args);

  const start_time = omitEmptyDateTime(payload.start_time);
  const end_time = omitEmptyDateTime(payload.end_time);

  const cleaned = {
    ...payload,
    ...(start_time !== undefined ? { start_time } : { start_time: null }),
    ...(end_time !== undefined ? { end_time } : { end_time: null }),
  };

  if (!cleaned.start_time) delete cleaned.start_time;
  if (!cleaned.end_time) delete cleaned.end_time;

  cleaned.workers = (cleaned.workers || []).map((w) => {
    const next = { ...w };
    if (!next.start_time) delete next.start_time;
    if (!next.end_time) delete next.end_time;

    // Persist a lightweight worker snapshot so resume can show names immediately
    // without waiting for common-resources / worker list loading.
    // Skip stub-shaped workers so drafts don't re-open as "Worker #<id>".
    if (next.worker_id != null && !next.worker) {
      const carers = args?.advanceCarers || [];
      const selected = args?.watchFormData?.advance_selected_workers || [];
      const row = carers.find(
        (c) =>
          c &&
          !c.is_open_slot &&
          (Number(c.worker_id) === Number(next.worker_id) ||
            Number(c?.worker?.id) === Number(next.worker_id))
      );
      const fromSelected =
        selected.find((sw) => Number(sw?.id) === Number(next.worker_id)) ||
        row?.worker ||
        null;
      if (
        fromSelected &&
        !assignmentNeedsWorkerLookup({
          worker_id: next.worker_id,
          worker: fromSelected,
        })
      ) {
        next.worker = {
          id: fromSelected.id ?? next.worker_id,
          uuid: fromSelected.uuid || fromSelected.value || null,
          first_name: fromSelected.first_name || null,
          last_name: fromSelected.last_name || null,
          photo:
            fromSelected.photo ||
            fromSelected.profile_picture ||
            null,
          label: fromSelected.label || null,
        };
      }
    } else if (
      next.worker &&
      assignmentNeedsWorkerLookup({
        worker_id: next.worker_id,
        worker: next.worker,
      })
    ) {
      delete next.worker;
    }

    return next;
  });

  // Persist participant snapshots (uuid + names) for instant resume labels.
  // Skip stub-shaped rows so drafts don't re-open as "Participant #<id>".
  const selectedParticipants =
    args?.watchFormData?.advance_selected_participants || [];
  cleaned.participants = (cleaned.participant_ids || [])
    .map((id) => {
      const fromSelected = selectedParticipants.find(
        (p) => Number(p?.id) === Number(id)
      );
      if (!fromSelected || participantNeedsLookup(fromSelected)) return null;
      const uuid = fromSelected.uuid || fromSelected.value || null;
      if (!uuid || isNumericStubId(uuid)) return null;
      return {
        id: fromSelected.id ?? id,
        uuid,
        first_name: fromSelected.first_name || null,
        last_name: fromSelected.last_name || null,
        profile_picture:
          fromSelected.profile_picture || fromSelected.photo || null,
        label: fromSelected.label || null,
      };
    })
    .filter(Boolean);

  // Persist home uuid/label so draft resume can skip the sil-homes list scan
  if (
    args?.watchFormData?.advance_sil_home_enabled &&
    cleaned.sil_home_id != null
  ) {
    if (args.watchFormData.advance_sil_home_uuid) {
      cleaned.sil_home_uuid = args.watchFormData.advance_sil_home_uuid;
    }
    if (args.watchFormData.advance_sil_home_label) {
      cleaned.sil_home_label = args.watchFormData.advance_sil_home_label;
    }
  }

  cleaned.participant_splits = (cleaned.participant_splits || []).map(
    (group) => ({
      ...group,
      splits: (group.splits || []).map((segment) => {
        const next = { ...segment };
        if (!next.start_time) delete next.start_time;
        if (!next.end_time) delete next.end_time;
        return next;
      }),
    })
  );

  return cleaned;
};

export const isAdvanceShiftDraft = (item) =>
  Boolean(item?.is_draft || item?.status === "draft");

const toDateKey = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

/** Drafts that belong on a schedule row for the given date. */
export const draftsForUserOnDate = (
  drafts,
  userItem,
  calendarDate,
  calendarView
) => {
  const dateKey = toDateKey(calendarDate);
  const userId = Number(userItem?.id);
  if (!dateKey || !userId) return [];

  return (drafts || []).filter((draft) => {
    if (toDateKey(draft?.shift_date) !== dateKey) return false;
    if (calendarView === "worker-view") {
      const ids = (draft.worker_ids || []).map(Number);
      return ids.includes(userId);
    }
    const ids = (draft.participant_ids || []).map(Number);
    return ids.includes(userId);
  });
};

/**
 * Drafts in the visible date range that have no matching IDs for the current
 * calendar view (still need a place to Resume / Discard).
 */
export const unmatchedDraftsForView = (drafts, calendarView, calendarDates) => {
  const dateSet = new Set(
    (calendarDates || []).map((d) => toDateKey(d)).filter(Boolean)
  );

  return (drafts || []).filter((draft) => {
    if (!dateSet.has(toDateKey(draft?.shift_date))) return false;
    if (calendarView === "worker-view") {
      return !(draft.worker_ids || []).length;
    }
    return !(draft.participant_ids || []).length;
  });
};

export const formatDraftTimeRange = (draft) => {
  const start = toClockTime(draft?.start_time);
  const end = toClockTime(draft?.end_time);
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  return "No time set";
};
